import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import { PermissionGate } from "@/components/PermissionGate";
import { SideNav } from "@/components/layout/SideNav";
import { TopNav } from "@/components/layout/TopNav";
import { ProjectNavFrame } from "@/components/layout/ProjectNavFrame";
import { PageFooter } from "@/components/PageFooter";
import { BranchSwitcher } from "@/components/BranchSwitcher";
import { useCompany } from "@/context/CompanyContext";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { useRoleTour, TOUR_MIN_WIDTH } from "@/tours/useRoleTour";

export function AppShell() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("dpr_sidebar_collapsed") === "true");
  const [mobileOpen, setMobileOpen] = useState(false);
  // The menus are memoised, so give them a new key when the company mode changes (one company or several).
  const { multiCompany } = useCompany() || {};
  const navKey = multiCompany ? "multi" : "single";

  /* First-time guided tour: starts once after login until the user has finished or closed it; "Take a tour" replays it.
     Never on phone widths. A collapsed sidebar is opened first, so every menu step has something to point at. */
  const { user } = useAuth();
  const { loading: permissionsLoading } = usePermissions();
  const { startTour, startGuide, startWorkflow, startJourney } = useRoleTour();
  const autoStarted = useRef(false);
  const launchTour = useCallback(() => {
    if (window.innerWidth < TOUR_MIN_WIDTH) return;
    let wait = 300;
    if (localStorage.getItem("dpr_sidebar_collapsed") === "true") {
      localStorage.setItem("dpr_sidebar_collapsed", "false");
      setCollapsed(false);
      wait = 700;                                   // the sidebar animates open
    }
    setTimeout(startTour, wait);
  }, [startTour]);
  useEffect(() => {
    if (autoStarted.current || !user || user.tour_completed_at || permissionsLoading) return;
    autoStarted.current = true;
    launchTour();
  }, [user, permissionsLoading, launchTour]);
  useEffect(() => {
    window.addEventListener("buildnexa:start-tour", launchTour);
    return () => window.removeEventListener("buildnexa:start-tour", launchTour);
  }, [launchTour]);
  useEffect(() => {                                   // the "Guide" button in the top bar: help for the module the user is in
    const onGuide = (e) => {
      const { moduleId, workflowId, journey, stageId } = e.detail || {};
      if (journey) startJourney(stageId); else if (workflowId) startWorkflow(moduleId, workflowId); else startGuide(moduleId);
    };
    window.addEventListener("buildnexa:start-guide", onGuide);
    return () => window.removeEventListener("buildnexa:start-guide", onGuide);
  }, [startGuide, startWorkflow, startJourney]);

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      localStorage.setItem("dpr_sidebar_collapsed", String(!c));
      return !c;
    });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <SideNav
        key={`side-${navKey}`}
        collapsed={collapsed}
        onToggleCollapse={toggleCollapsed}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopNav key={`top-${navKey}`} onMobileMenuOpen={() => setMobileOpen(true)} />
        <BranchSwitcher />
        <ProjectNavFrame>
        <main className="min-w-0 flex-1 overflow-y-auto bg-[#F3F7F9] p-4 dark:bg-transparent sm:p-6">
          <div className="space-y-5">
            <PermissionGate><Outlet /></PermissionGate>
            <PageFooter />
          </div>
        </main>
        </ProjectNavFrame>
      </div>
    </div>
  );
}
